import config from '../config/environment';
import supabase from '../config/supabase';
import {
  parseRecentBodyPhotoAssessments,
  type RecentBodyPhotoAssessmentRow,
  type RecentBodyPhotoAssessmentsDashboard,
  type RecentBodyPhotoAssessmentsQuery,
} from '../utils/recentBodyPhotoAssessments';
import { handleServiceError } from '../utils/errorHandler';

class RecentBodyPhotoAssessmentsServiceClass {
  async getDashboard(query: RecentBodyPhotoAssessmentsQuery): Promise<RecentBodyPhotoAssessmentsDashboard> {
    const token = await this.getAccessToken();
    const url = this.buildUrl(query);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });
    const body = await response.json().catch(() => ({})) as { error?: string };

    if (!response.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao carregar as fotos recentes'),
        'carregar as fotos recentes',
      );
    }

    const snapshot = parseRecentBodyPhotoAssessments(body);
    if (!snapshot) {
      handleServiceError(
        new Error('Erro ao carregar as fotos recentes'),
        'carregar as fotos recentes',
      );
    }

    return snapshot;
  }

  async setReviewed(
    studentId: number,
    assessmentDate: string,
    reviewed: boolean,
  ): Promise<Pick<RecentBodyPhotoAssessmentRow, 'reviewedAt' | 'reviewed'>> {
    const token = await this.getAccessToken();
    const httpResponse = await fetch(`${config.supabaseFunctionsUrl}/recent-body-photo-assessments`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({ studentId, assessmentDate, reviewed }),
    });
    const body = await httpResponse.json().catch(() => ({})) as {
      error?: string;
      reviewedAt?: unknown;
      reviewed?: unknown;
    };

    if (!httpResponse.ok) {
      handleServiceError(
        new Error(body.error || 'Erro ao atualizar a avaliação'),
        'atualizar a avaliação',
      );
    }

    const reviewedAt = typeof body.reviewedAt === 'string' && body.reviewedAt.trim()
      ? body.reviewedAt
      : null;

    return {
      reviewedAt,
      reviewed: body.reviewed === true,
    };
  }

  private buildUrl(query: RecentBodyPhotoAssessmentsQuery): URL {
    const url = new URL(`${config.supabaseFunctionsUrl}/recent-body-photo-assessments`);
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));

    if (query.dateStart && query.dateEnd) {
      url.searchParams.set('dateStart', query.dateStart);
      url.searchParams.set('dateEnd', query.dateEnd);
    }

    const search = query.searchName.trim();
    if (search) {
      url.searchParams.set('search', search);
    }

    return url;
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }
}

export const RecentBodyPhotoAssessmentsService = new RecentBodyPhotoAssessmentsServiceClass();
